// Editor-only safe-zone overlay for Instagram Portrait (1080x1350) templates: four
// semi-transparent margin bands plus a label, injected on load and stripped before save.

import { isAlive } from 'mobx-state-tree';

import { getEffectivePageSize, type PageSize } from './listingOverlay';

const SAFE_ZONE_ID = 'ig_safezone_overlay';

const IG_PORTRAIT_WIDTH = 1080;
const IG_PORTRAIT_HEIGHT = 1350;
const SAFE_ZONE_INSET_PX = 35;
const SAFE_ZONE_FILL = 'rgba(255, 0, 0, 0.25)';

const SAFE_ZONE_NETWORK = 'instagram';

const isIgPortraitSize = (width: number, height: number): boolean =>
	width === IG_PORTRAIT_WIDTH && height === IG_PORTRAIT_HEIGHT;

const getPageNetwork = (page: any) => page?.custom?.network;

// Show on Instagram pages and on pages with no network marker; suppress on
// pages explicitly created for another network (Facebook, LinkedIn, X).
const isSafeZonePage = (page: any, width: number, height: number): boolean => {
	if (!isIgPortraitSize(width, height)) return false;
	const network = getPageNetwork(page);
	return network == null || network === SAFE_ZONE_NETWORK;
};

const bandDefaults = {
	type: 'figure',
	subType: 'rect',
	name: SAFE_ZONE_ID,
	rotation: 0,
	opacity: 1,
	visible: true,
	fill: SAFE_ZONE_FILL,
	stroke: SAFE_ZONE_FILL,
	strokeWidth: 0,
	cornerRadius: 0,
	selectable: false,
	alwaysOnTop: true,
	showInExport: false,
	removable: false,
	draggable: false,
	resizable: false,
	contentEditable: false,
	styleEditable: false,
};

const labelDefaults = {
	type: 'text',
	name: SAFE_ZONE_ID,
	rotation: 0,
	opacity: 1,
	visible: true,
	fontFamily: 'Roboto',
	fontStyle: 'normal',
	fontWeight: 'bold',
	textDecoration: '',
	lineHeight: 1.2,
	letterSpacing: 0,
	align: 'center',
	verticalAlign: 'top',
	fill: 'rgba(255, 255, 255, 1)',
	stroke: 'black',
	strokeWidth: 0,
	placeholder: '',
	backgroundEnabled: false,
	selectable: false,
	alwaysOnTop: true,
	showInExport: false,
	removable: false,
	draggable: false,
	resizable: false,
	contentEditable: false,
	styleEditable: false,
};

// idSuffix keeps element ids unique per page; Polotno ids must not collide across
// pages, so multi-page documents pass the page id here.
const buildSafeZoneElements = (
	width: number,
	height: number,
	idSuffix = '',
): any[] => {
	const inset = SAFE_ZONE_INSET_PX;
	const innerH = height - inset * 2;

	return [
		{ ...bandDefaults, id: `${SAFE_ZONE_ID}_top${idSuffix}`, x: 0, y: 0, width, height: inset },
		{ ...bandDefaults, id: `${SAFE_ZONE_ID}_bottom${idSuffix}`, x: 0, y: height - inset, width, height: inset },
		{ ...bandDefaults, id: `${SAFE_ZONE_ID}_left${idSuffix}`, x: 0, y: inset, width: inset, height: innerH },
		{ ...bandDefaults, id: `${SAFE_ZONE_ID}_right${idSuffix}`, x: width - inset, y: inset, width: inset, height: innerH },
		{
			...labelDefaults,
			id: `${SAFE_ZONE_ID}_label${idSuffix}`,
			x: 0,
			y: 4,
			width,
			height: inset - 6,
			fontSize: 9,
			lineHeight: 1.1,
			text: 'OUTSIDE SAFE ZONE\nContent may be covered or cut off in feeds',
		},
	];
};

const pageIdSuffix = (page: any): string => (page?.id ? `_${page.id}` : '');

const isSafeZoneElement = (element: any): boolean =>
	element?.name === SAFE_ZONE_ID ||
	String(element?.id || '').startsWith(SAFE_ZONE_ID);

const removeSafeZoneOverlay = (json: any): any => {
	json?.pages?.forEach((page) => {
		page.children = (page.children || []).filter(
			(child) =>
				child.name !== SAFE_ZONE_ID &&
				!String(child.id || '').startsWith(SAFE_ZONE_ID),
		);
	});

	return json;
};

// Evaluated per page so mixed-format documents only mark their IG portrait page.
const addSafeZoneOverlay = (json: any): any => {
	removeSafeZoneOverlay(json);

	json?.pages?.forEach((page) => {
		const { width, height } = getEffectivePageSize(json, page);
		if (!isSafeZonePage(page, width, height)) {
			return;
		}

		page.children = [
			...(page.children || []),
			...buildSafeZoneElements(width, height, pageIdSuffix(page)),
		];
	});

	return json;
};

const pageEffectiveSize = (store: any, page: any): PageSize => ({
	width: page?.computedWidth ?? store?.width,
	height: page?.computedHeight ?? store?.height,
});

// Reconcile the overlay against a live Polotno store so pages added or duplicated
// after load (which never pass through addSafeZoneOverlay) carry a correct overlay,
// and pages resized/retargeted away from IG portrait lose it. A page counts as
// in-sync only when the safe-zone elements present are exactly the per-page ids
// expected for it; anything else (missing, or ids carried over from a duplicated
// page) is rebuilt with fresh page-scoped ids, so cross-page id collisions can never
// accumulate. Idempotent and history-free, so it is safe to attach to the store
// 'change' event: it no-ops once every page is in sync, and its own edits do not
// enter the user's undo/redo stack.
const syncSafeZoneOverlayInStore = (store: any): void => {
	if (!store?.pages?.length) return;

	const additions = [];
	const idsToRemove = [];

	store.pages.forEach((page) => {
		if (!isAlive(page)) return;

		const { width, height } = pageEffectiveSize(store, page);
		const present = (page.children || []).filter(isSafeZoneElement);

		if (!isSafeZonePage(page, width, height)) {
			// Not eligible: strip any overlay that is present.
			if (present.length > 0) idsToRemove.push(...present.map((el) => el.id));
			return;
		}

		const elements = buildSafeZoneElements(width, height, pageIdSuffix(page));
		const expectedIds = elements.map((el) => el.id);
		const inSync =
			present.length === expectedIds.length &&
			present.every((el) => expectedIds.includes(el.id));
		if (inSync) return;

		// Missing overlay, or stale ids copied onto a duplicated page: rebuild it.
		if (present.length > 0) idsToRemove.push(...present.map((el) => el.id));
		additions.push({ page, elements });
	});

	if (additions.length === 0 && idsToRemove.length === 0) return;

	// Remove stale/ineligible overlay elements before re-adding: every id here is
	// page-unique, so a store-wide delete only ever touches its own page.
	store.history.ignore(() => {
		if (idsToRemove.length > 0) store.deleteElements(idsToRemove);
		additions.forEach(({ page, elements }) => {
			elements.forEach((el) => page.addElement(el, { skipSelect: true }));
		});
	});
};

export {
	addSafeZoneOverlay,
	removeSafeZoneOverlay,
	isSafeZoneElement,
	syncSafeZoneOverlayInStore,
};
